import { ReactElement, ReactNode, use, useEffect } from 'react';
import GetOrganisationUnitTreeQuery from 'shared/features/organisation/models/get-organisation-unit-tree-query';
import OrganisationUnitTreeProviderContext from 'shared/features/organisation/providers/contexts/organisation-unit-tree-provider-context';
import {
  useGetMyOrganisationUnitTree,
  useGetOrganisationUnitTree,
  useGetOrganisationUnitTreeWithFaults,
  useLazyGetOrganisationUnitTreeForLinkedIntegration,
  useLazyGetOrganisationUnitTreeForLocation,
} from 'shared/features/organisation/providers/organisation-unit-queries';
import useOrganisationUnitTreeState, {
  OrganisationUnitTreeProviderState,
} from 'shared/features/organisation/providers/states/organisation-unit-tree-state';

type OrganisationUnitTreeProviderProps = {
  children: ReactNode;
  excludeBranchesWithoutResidents?: boolean;
};

type OrganisationUnitTreeProviderLinkedIntegrationProps = OrganisationUnitTreeProviderProps & {
  gatewayId?: string;
  systemId?: string;
};

type OrganisationUnitTreeForLocationProviderProps = OrganisationUnitTreeProviderProps & {
  organisationUnitId?: string;
};

const AllOrganisationUnitTreeProvider = (props: OrganisationUnitTreeProviderProps): ReactElement => {
  const getOrganistionUnitTreeQuery = {
    excludeBranchesWithoutResidents: props.excludeBranchesWithoutResidents ?? false,
  } as GetOrganisationUnitTreeQuery;
  const getOrganisationUnitTreeQuery = useGetOrganisationUnitTree(getOrganistionUnitTreeQuery);

  const providerValue = useOrganisationUnitTreeState();

  useEffect(() => {
    providerValue.setOrganisationUnitTreeState?.({
      ...getOrganisationUnitTreeQuery,
      organisationUnitTree: getOrganisationUnitTreeQuery.data,
      isFetching: getOrganisationUnitTreeQuery.isFetching,
      isLoading: getOrganisationUnitTreeQuery.isLoading,
      isSuccess: getOrganisationUnitTreeQuery.isSuccess,
      error: getOrganisationUnitTreeQuery.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getOrganisationUnitTreeQuery.data,
    getOrganisationUnitTreeQuery.isFetching,
    getOrganisationUnitTreeQuery.isLoading,
    getOrganisationUnitTreeQuery.isSuccess,
    getOrganisationUnitTreeQuery.error,
  ]);

  return (
    <OrganisationUnitTreeProviderContext.Provider value={providerValue}>{props.children}</OrganisationUnitTreeProviderContext.Provider>
  );
};

const MyOrganisationUnitTreeProvider = (props: OrganisationUnitTreeProviderProps): ReactElement => {
  const getOrganistionUnitTreeQuery = {
    excludeBranchesWithoutResidents: props.excludeBranchesWithoutResidents ?? false,
  } as GetOrganisationUnitTreeQuery;
  const getMyOrganisationUnitTreeQuery = useGetMyOrganisationUnitTree(getOrganistionUnitTreeQuery);
  const providerValue = useOrganisationUnitTreeState();

  useEffect(() => {
    providerValue.setOrganisationUnitTreeState?.({
      ...getMyOrganisationUnitTreeQuery,
      organisationUnitTree: getMyOrganisationUnitTreeQuery.data,
      isFetching: getMyOrganisationUnitTreeQuery.isFetching,
      isLoading: getMyOrganisationUnitTreeQuery.isLoading,
      isSuccess: getMyOrganisationUnitTreeQuery.isSuccess,
      error: getMyOrganisationUnitTreeQuery.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getMyOrganisationUnitTreeQuery.data,
    getMyOrganisationUnitTreeQuery.isFetching,
    getMyOrganisationUnitTreeQuery.isLoading,
    getMyOrganisationUnitTreeQuery.isSuccess,
    getMyOrganisationUnitTreeQuery.error,
  ]);

  return (
    <OrganisationUnitTreeProviderContext.Provider value={providerValue}>{props.children}</OrganisationUnitTreeProviderContext.Provider>
  );
};

const OrganisationUnitTreeForForLinkedIntegrationProvider = ({
  children,
  gatewayId,
  systemId,
}: OrganisationUnitTreeProviderLinkedIntegrationProps): ReactElement => {
  const getOrganisationUnitTreeQuery = useLazyGetOrganisationUnitTreeForLinkedIntegration({
    gatewayId,
    systemId,
  });

  const providerValue = useOrganisationUnitTreeState();

  useEffect(() => {
    if (gatewayId || systemId) {
      getOrganisationUnitTreeQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId, systemId]);

  useEffect(() => {
    providerValue.setOrganisationUnitTreeState?.({
      ...getOrganisationUnitTreeQuery,
      organisationUnitTree: getOrganisationUnitTreeQuery.data,
      isFetching: getOrganisationUnitTreeQuery.isFetching,
      isLoading: getOrganisationUnitTreeQuery.isLoading,
      isSuccess: getOrganisationUnitTreeQuery.isSuccess,
      error: getOrganisationUnitTreeQuery.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getOrganisationUnitTreeQuery.data,
    getOrganisationUnitTreeQuery.isFetching,
    getOrganisationUnitTreeQuery.isLoading,
    getOrganisationUnitTreeQuery.isSuccess,
    getOrganisationUnitTreeQuery.error,
  ]);

  return <OrganisationUnitTreeProviderContext.Provider value={providerValue}>{children}</OrganisationUnitTreeProviderContext.Provider>;
};

const OrganisationUnitTreeWithFaultsProvider = (props: OrganisationUnitTreeProviderProps): ReactElement => {
  const getOrganisationUnitTreeQuery = useGetOrganisationUnitTreeWithFaults();
  const providerValue = useOrganisationUnitTreeState();

  useEffect(() => {
    providerValue.setOrganisationUnitTreeState?.({
      ...getOrganisationUnitTreeQuery,
      organisationUnitTree: getOrganisationUnitTreeQuery.data,
      isFetching: getOrganisationUnitTreeQuery.isFetching,
      isLoading: getOrganisationUnitTreeQuery.isLoading,
      isSuccess: getOrganisationUnitTreeQuery.isSuccess,
      error: getOrganisationUnitTreeQuery.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getOrganisationUnitTreeQuery.data,
    getOrganisationUnitTreeQuery.isFetching,
    getOrganisationUnitTreeQuery.isLoading,
    getOrganisationUnitTreeQuery.isSuccess,
    getOrganisationUnitTreeQuery.error,
  ]);

  return (
    <OrganisationUnitTreeProviderContext.Provider value={providerValue}>{props.children}</OrganisationUnitTreeProviderContext.Provider>
  );
};

const OrganisationUnitTreeForLocationProvider = ({
  children,
  organisationUnitId,
}: OrganisationUnitTreeForLocationProviderProps): ReactElement => {
  const getOrganisationUnitTreeQuery = useLazyGetOrganisationUnitTreeForLocation(organisationUnitId ?? '');

  const providerValue = useOrganisationUnitTreeState();

  useEffect(() => {
    if (organisationUnitId) {
      getOrganisationUnitTreeQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationUnitId]);

  useEffect(() => {
    providerValue.setOrganisationUnitTreeState?.({
      ...getOrganisationUnitTreeQuery,
      organisationUnitTree: getOrganisationUnitTreeQuery.data,
      isFetching: getOrganisationUnitTreeQuery.isFetching,
      isLoading: getOrganisationUnitTreeQuery.isLoading,
      isSuccess: getOrganisationUnitTreeQuery.isSuccess,
      error: getOrganisationUnitTreeQuery.error,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getOrganisationUnitTreeQuery.data,
    getOrganisationUnitTreeQuery.isFetching,
    getOrganisationUnitTreeQuery.isLoading,
    getOrganisationUnitTreeQuery.isSuccess,
    getOrganisationUnitTreeQuery.error,
  ]);

  return <OrganisationUnitTreeProviderContext.Provider value={providerValue}>{children}</OrganisationUnitTreeProviderContext.Provider>;
};

const useOrganisationUnitTreeContext = (): OrganisationUnitTreeProviderState => use(OrganisationUnitTreeProviderContext);

const OrganisationUnitTreeProvider = {
  All: AllOrganisationUnitTreeProvider,
  AccessRightsOnly: MyOrganisationUnitTreeProvider,
  ForLinkedIntegration: OrganisationUnitTreeForForLinkedIntegrationProvider,
  WithFaults: OrganisationUnitTreeWithFaultsProvider,
  ForLocation: OrganisationUnitTreeForLocationProvider,
};

export { OrganisationUnitTreeProvider, useOrganisationUnitTreeContext };
